import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Chip, Badge,
  LinearProgress, Alert, IconButton, Tooltip, Select,
  MenuItem, FormControl, InputLabel, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress
} from '@mui/material';
import RefreshIcon      from '@mui/icons-material/Refresh';
import RouterIcon       from '@mui/icons-material/Router';
import WifiIcon         from '@mui/icons-material/Wifi';
import AddIcon          from '@mui/icons-material/Add';
import DeleteIcon       from '@mui/icons-material/Delete';
import RestartAltIcon   from '@mui/icons-material/RestartAlt';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import WifiOffIcon      from '@mui/icons-material/WifiOff';
import AssignmentIcon   from '@mui/icons-material/Assignment';
import EditIcon         from '@mui/icons-material/Edit';
import { sensorsAPI, sitesAPI } from '../services/api';
import api from '../services/api';

function SensorCard({ sensor, onDelete, onReboot, onOta, onEdit }) {
  const isOnline  = sensor.is_online;
  const lastSeen  = sensor.last_seen
    ? new Date(sensor.last_seen).toLocaleTimeString('fr-FR')
    : 'Jamais';
  const roleColor = {
    ENTRANCE: '#1F4E79', MIDDLE: '#2E75B6',
    END: '#4CAF50', CUSTOM: '#FF9800'
  }[sensor.role] || '#666';

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      border: `2px solid ${isOnline ? '#4CAF5030' : '#F4433630'}` }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${roleColor}15` }}>
              <RouterIcon sx={{ color: roleColor, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography fontWeight={700} fontSize="0.9rem">{sensor.sensor_id}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                <Chip label={sensor.role} size="small"
                  sx={{ bgcolor: `${roleColor}15`, color: roleColor, fontSize: '0.7rem', height: 20 }} />
                {sensor.zone_name && (
                  <Chip label={sensor.zone_name} size="small"
                    sx={{ bgcolor: '#E91E6315', color: '#E91E63', fontSize: '0.7rem', height: 20 }} />
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label={isOnline ? 'En ligne' : 'Hors ligne'} size="small"
              sx={{ bgcolor: isOnline ? '#4CAF5020' : '#F4433620',
                color: isOnline ? '#4CAF50' : '#F44336', fontWeight: 600 }} />
            <Tooltip title="Modifier le role">
              <IconButton size="small" color="primary" onClick={() => onEdit(sensor)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer">
              <IconButton size="small" color="error" onClick={() => onDelete(sensor.sensor_id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[
            ['Position', `X:${sensor.x_cm || 0}cm Y:${sensor.y_cm || 0}cm${sensor.is_master ? ' (Maitre)' : ''}`],
            ['Firmware',  sensor.firmware_ver || 'N/A'],
            ['Dernier signal', lastSeen],
          ].map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">{k}</Typography>
              <Typography variant="body2" fontWeight={600}>{v}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WifiIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">Signal</Typography>
            </Box>
            <Typography variant="caption" color={isOnline ? '#4CAF50' : '#F44336'}>
              {isOnline ? 'Actif' : 'Inactif'}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={isOnline ? 85 : 0}
            sx={{ height: 6, borderRadius: 3, bgcolor: '#E0E0E0',
              '& .MuiLinearProgress-bar': {
                bgcolor: isOnline ? '#4CAF50' : '#F44336', borderRadius: 3 }}} />
        </Box>

        {/* Actions OTA / Reboot */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Tooltip title="Redemarrer a distance (effectif au prochain push ~5 min, fenetre OTA 30 sec)">
            <Button size="small" variant="outlined" startIcon={<RestartAltIcon />}
              onClick={() => onReboot(sensor.sensor_id)}
              sx={{ flex: 1, fontSize: '0.75rem', borderColor: '#FF9800', color: '#FF9800',
                '&:hover': { bgcolor: '#FF980010', borderColor: '#FF9800' } }}>
              Reboot
            </Button>
          </Tooltip>
          <Tooltip title="Mettre a jour le firmware via OTA (selectionner un .bin PlatformIO)">
            <Button size="small" variant="outlined" startIcon={<SystemUpdateIcon />}
              onClick={() => onOta(sensor.sensor_id)}
              sx={{ flex: 1, fontSize: '0.75rem', borderColor: '#1F4E79', color: '#1F4E79',
                '&:hover': { bgcolor: '#1F4E7910', borderColor: '#1F4E79' } }}>
              OTA
            </Button>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Supervision() {
  const [sensors,    setSensors]    = useState([]);
  const [sites,      setSites]      = useState([]);
  const [siteId,     setSiteId]     = useState('');
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [addDialog,    setAddDialog]    = useState(false);
  const [otaDialog,    setOtaDialog]    = useState({ open: false, sensorId: '', file: null, uploading: false });
  const [unassigned,   setUnassigned]   = useState([]);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignForm,   setAssignForm]   = useState({ site_id: '', role: 'ENTRANCE', display_name: '' });
  const [editDialog,   setEditDialog]   = useState({ open: false, sensor: null });
  const [editForm,     setEditForm]     = useState({ role: 'ENTRANCE', display_name: '' });
  const [form, setForm] = useState({
    sensor_id: '', site_id: '', role: 'ENTRANCE', firmware_ver: ''
  });
  const fileInputRef = useRef();

  const loadSensors = () => {
    setLoading(true);
    Promise.all([sensorsAPI.status(), sensorsAPI.unassigned()])
      .then(([r, u]) => {
        let data = r.data.sensors || [];
        if (siteId) data = data.filter(s => s.site_id === siteId);
        setSensors(data);
        setUnassigned(u.data.sensors || []);
        setLastUpdate(new Date().toLocaleTimeString('fr-FR'));
        setError('');
      })
      .catch(() => setError('Impossible de charger les capteurs'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    sitesAPI.list().then(r => setSites(r.data.sites || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadSensors();
    const interval = setInterval(loadSensors, 30000);
    return () => clearInterval(interval);
  }, [siteId]);

  const addSensor = async () => {
    setLoading(true);
    try {
      await sensorsAPI.create({ ...form, site_id: form.site_id || siteId });
      setSuccess(`Capteur ${form.sensor_id} ajoute`);
      setAddDialog(false);
      setForm({ sensor_id: '', site_id: '', role: 'ENTRANCE', firmware_ver: '' });
      loadSensors();
    } catch(e) {
      setError(e.response?.data?.detail || 'Erreur ajout capteur');
    } finally { setLoading(false); }
  };

  const deleteSensor = async (sensorId) => {
    if (!window.confirm(`Supprimer le capteur ${sensorId} ?`)) return;
    try {
      await sensorsAPI.delete(sensorId);
      setSuccess(`Capteur ${sensorId} supprime`);
      loadSensors();
    } catch { setError('Erreur suppression'); }
  };

  const handleReboot = async (sensorId) => {
    if (!window.confirm(`Envoyer un reboot au capteur ${sensorId} ?\n\nLe capteur redemarrera au prochain push (~5 min). La fenetre OTA s'ouvrira pendant 30 sec.`)) return;
    try {
      await sensorsAPI.reboot(sensorId);
      setSuccess(`Reboot programme pour ${sensorId} — effectif au prochain push tracking`);
    } catch(e) {
      setError(e.response?.data?.detail || 'Erreur reboot');
    }
  };

  const handleOtaOpen = (sensorId) => {
    setOtaDialog({ open: true, sensorId, file: null, uploading: false });
  };

  const handleOtaUpload = async () => {
    if (!otaDialog.file) return;
    setOtaDialog(d => ({ ...d, uploading: true }));
    try {
      const formData = new FormData();
      formData.append('firmware', otaDialog.file);
      await sensorsAPI.ota(otaDialog.sensorId, formData);
      setSuccess(`OTA programme pour ${otaDialog.sensorId} (${(otaDialog.file.size / 1024).toFixed(0)} KB) — MAJ au prochain push`);
      setOtaDialog({ open: false, sensorId: '', file: null, uploading: false });
    } catch(e) {
      setError(e.response?.data?.detail || 'Erreur upload OTA');
      setOtaDialog(d => ({ ...d, uploading: false }));
    }
  };

  const handleEdit = async () => {
    if (!editDialog.sensor) return;
    try {
      await api.put('/v1/sensors/' + editDialog.sensor.sensor_id + '/role', editForm);
      setSuccess('Capteur ' + editDialog.sensor.sensor_id + ' mis a jour');
      setEditDialog({ open: false, sensor: null });
      loadSensors();
    } catch(e) {
      setError(e.response?.data?.detail || 'Erreur mise a jour');
    }
  };

  const handleAssign = async () => {
    if (!assignTarget) return;
    setLoading(true);
    try {
      await sensorsAPI.assign(assignTarget.sensor_id, assignForm);
      setSuccess(`Capteur ${assignTarget.sensor_id} affecte a ${assignForm.site_id}`);
      setAssignDialog(false);
      setAssignTarget(null);
      loadSensors();
    } catch(e) {
      setError(e.response?.data?.detail || 'Erreur affectation');
    } finally { setLoading(false); }
  };

  const online  = sensors.filter(s => s.is_online).length;
  const offline = sensors.filter(s => !s.is_online).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1F4E79">Supervision capteurs</Typography>
          <Typography variant="body2" color="text.secondary">
            {lastUpdate ? `Mise a jour : ${lastUpdate}` : 'Chargement...'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Boutique</InputLabel>
            <Select value={siteId} label="Boutique"
              onChange={e => setSiteId(e.target.value)}>
              <MenuItem value="">Toutes</MenuItem>
              {sites.map(s => <MenuItem key={s.site_id} value={s.site_id}>{s.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { setForm(f => ({...f, site_id: siteId})); setAddDialog(true); }}
            sx={{ bgcolor: '#1F4E79', '&:hover': { bgcolor: '#163a5f' } }}>
            Ajouter
          </Button>
          <Tooltip title="Actualiser">
            <IconButton onClick={loadSensors} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Capteurs non affectes */}
      {unassigned.length > 0 && (
        <Card sx={{ borderRadius: 3, mb: 3, border: '2px solid #FF980050', bgcolor: '#FFF8E1' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Badge badgeContent={unassigned.length} color="warning">
                <WifiOffIcon sx={{ color: '#E65100' }} />
              </Badge>
              <Typography fontWeight={700} color="#E65100">
                Nouveaux capteurs detectes — non affectes
              </Typography>
            </Box>
            <Grid container spacing={1.5}>
              {unassigned.map(s => (
                <Grid item xs={12} sm={6} md={4} key={s.sensor_id}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #FFB30060' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography fontWeight={700} fontSize="0.85rem">{s.sensor_id}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            fw {s.firmware_ver || 'N/A'} &bull; vu {s.last_seen ? new Date(s.last_seen).toLocaleTimeString('fr-FR') : 'jamais'}
                          </Typography>
                        </Box>
                        <Button size="small" variant="contained" startIcon={<AssignmentIcon />}
                          onClick={() => {
                            setAssignTarget(s);
                            setAssignForm({ site_id: '', role: 'ENTRANCE', display_name: s.sensor_id });
                            setAssignDialog(true);
                          }}
                          sx={{ bgcolor: '#E65100', '&:hover': { bgcolor: '#BF360C' }, fontSize: '0.72rem' }}>
                          Affecter
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: sensors.length, color: '#1F4E79' },
          { label: 'En ligne', value: online, color: '#4CAF50' },
          { label: 'Hors ligne', value: offline, color: '#F44336' },
        ].map(stat => (
          <Grid item xs={4} key={stat.label}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', textAlign: 'center' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h4" fontWeight={700} color={stat.color}>{stat.value}</Typography>
                <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {sensors.length === 0 && !loading ? (
        <Alert severity="info">Aucun capteur enregistre. Cliquez sur "Ajouter" pour en creer un.</Alert>
      ) : (
        <Grid container spacing={2}>
          {sensors.map(sensor => (
            <Grid item xs={12} sm={6} md={4} key={sensor.sensor_id}>
              <SensorCard sensor={sensor} onDelete={deleteSensor}
                onReboot={handleReboot} onOta={handleOtaOpen}
                onEdit={s => { setEditDialog({ open: true, sensor: s }); setEditForm({ role: s.role, display_name: s.display_name || '' }); }} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog ajout capteur */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un capteur</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Sensor ID" required
                placeholder="Ex: REDIA-ZARA-CASA-A"
                value={form.sensor_id}
                onChange={e => setForm(p => ({...p, sensor_id: e.target.value}))} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Boutique</InputLabel>
                <Select value={form.site_id} label="Boutique"
                  onChange={e => setForm(p => ({...p, site_id: e.target.value}))}>
                  {sites.map(s => <MenuItem key={s.site_id} value={s.site_id}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select value={form.role} label="Role"
                  onChange={e => setForm(p => ({...p, role: e.target.value}))}>
                  {['ENTRANCE', 'MIDDLE', 'END', 'CUSTOM'].map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Version firmware (optionnel)"
                placeholder="1.0.0"
                value={form.firmware_ver}
                onChange={e => setForm(p => ({...p, firmware_ver: e.target.value}))} />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2, fontSize: '0.8rem' }}>
            Le capteur ENTRANCE sera automatiquement defini comme capteur maitre (origine 0,0).
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={addSensor}
            disabled={loading || !form.sensor_id || !form.site_id}
            startIcon={<AddIcon />}
            sx={{ bgcolor: '#1F4E79' }}>
            Ajouter le capteur
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog OTA */}
      <Dialog open={otaDialog.open}
        onClose={() => !otaDialog.uploading && setOtaDialog(d => ({...d, open: false}))}
        maxWidth="sm" fullWidth>
        <DialogTitle>Mise a jour OTA — {otaDialog.sensorId}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}>
            Le capteur redemarrera au prochain push (~5 min). La capture WiFi s'arretera ~30 sec pendant le flash.
          </Alert>
          <Box sx={{ border: '2px dashed #1F4E7940', borderRadius: 2, p: 3, textAlign: 'center',
            cursor: 'pointer', bgcolor: otaDialog.file ? '#E3F2FD' : 'transparent' }}
            onClick={() => fileInputRef.current?.click()}>
            <input ref={fileInputRef} type="file" accept=".bin" style={{ display: 'none' }}
              onChange={e => setOtaDialog(d => ({ ...d, file: e.target.files[0] || null }))} />
            <SystemUpdateIcon sx={{ fontSize: 40, color: '#1F4E79', mb: 1 }} />
            {otaDialog.file ? (
              <Box>
                <Typography fontWeight={600} color="#1F4E79">{otaDialog.file.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {(otaDialog.file.size / 1024).toFixed(0)} KB
                </Typography>
              </Box>
            ) : (
              <Typography color="text.secondary">Cliquer pour selectionner un .bin</Typography>
            )}
          </Box>
          {otaDialog.file && (
            <Alert severity="info" sx={{ mt: 2, fontSize: '0.8rem' }}>
              Firmware : <strong>{otaDialog.file.name}</strong> ({(otaDialog.file.size / 1024).toFixed(0)} KB)
              sera deploye sur <strong>{otaDialog.sensorId}</strong>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOtaDialog(d => ({...d, open: false}))} disabled={otaDialog.uploading}>
            Annuler
          </Button>
          <Button variant="contained" onClick={handleOtaUpload}
            disabled={!otaDialog.file || otaDialog.uploading}
            startIcon={otaDialog.uploading ? <CircularProgress size={16} color="inherit" /> : <SystemUpdateIcon />}
            sx={{ bgcolor: '#1F4E79' }}>
            {otaDialog.uploading ? 'Upload...' : 'Lancer OTA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog edition role capteur */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, sensor: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Modifier {editDialog.sensor?.sensor_id}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select value={editForm.role} label="Role"
                  onChange={e => setEditForm(p => ({...p, role: e.target.value}))}>
                  {['ENTRANCE', 'MIDDLE', 'END', 'CUSTOM'].map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Nom d affichage"
                value={editForm.display_name}
                onChange={e => setEditForm(p => ({...p, display_name: e.target.value}))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialog({ open: false, sensor: null })}>Annuler</Button>
          <Button variant="contained" onClick={handleEdit} sx={{ bgcolor: '#1F4E79' }}>
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog affectation capteur */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Affecter {assignTarget?.sensor_id}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Nom d'affichage (optionnel)"
                value={assignForm.display_name}
                onChange={e => setAssignForm(p => ({...p, display_name: e.target.value}))} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Boutique</InputLabel>
                <Select value={assignForm.site_id} label="Boutique"
                  onChange={e => setAssignForm(p => ({...p, site_id: e.target.value}))}>
                  {sites.map(s => <MenuItem key={s.site_id} value={s.site_id}>{s.name || s.site_id}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select value={assignForm.role} label="Role"
                  onChange={e => setAssignForm(p => ({...p, role: e.target.value}))}>
                  {['ENTRANCE', 'MIDDLE', 'END', 'CUSTOM'].map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2, fontSize: '0.8rem' }}>
            Apres affectation, configurer le WiFi via l'AP du capteur puis lancer la calibration.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAssignDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleAssign}
            disabled={loading || !assignForm.site_id}
            startIcon={<AssignmentIcon />}
            sx={{ bgcolor: '#E65100' }}>
            Affecter le capteur
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
